import type { FieldPack } from '../../../../core/fields/types';
import { FieldType } from '../../../../core/fields/types';

export const socialWorkFieldPack: FieldPack = {
  id: 'genogram-social-work',
  name: 'Social Work & Services',
  description: 'Fields for social work assessment and service coordination',
  version: '1.0.0',
  dependencies: ['genogram-base'],
  fields: [
    {
      id: 'housingStatus',
      label: 'fields.social.housingStatus.label',
      description: 'fields.social.housingStatus.description',
      type: FieldType.SELECT,
      tags: ['social', 'living-situation'],
      priority: 95,
      options: [
        { value: 'owned', label: 'fields.social.housingStatus.options.owned' },
        { value: 'rented', label: 'fields.social.housingStatus.options.rented' },
        { value: 'with_family', label: 'fields.social.housingStatus.options.with_family' },
        { value: 'shelter', label: 'fields.social.housingStatus.options.shelter' },
        { value: 'transitional', label: 'fields.social.housingStatus.options.transitional' },
        { value: 'homeless', label: 'fields.social.housingStatus.options.homeless' },
        { value: 'assisted_living', label: 'fields.social.housingStatus.options.assisted_living' },
        { value: 'nursing_home', label: 'fields.social.housingStatus.options.nursing_home' },
      ],
      namespace: 'social',
    },
    {
      id: 'employmentStatus',
      label: 'fields.social.employmentStatus.label',
      description: 'fields.social.employmentStatus.description',
      type: FieldType.SELECT,
      tags: ['employment', 'professional'],
      priority: 90,
      options: [
        { value: 'employed_full', label: 'fields.social.employmentStatus.options.employed_full' },
        { value: 'employed_part', label: 'fields.social.employmentStatus.options.employed_part' },
        { value: 'unemployed', label: 'fields.social.employmentStatus.options.unemployed' },
        { value: 'disabled', label: 'fields.social.employmentStatus.options.disabled' },
        { value: 'retired', label: 'fields.social.employmentStatus.options.retired' },
        { value: 'student', label: 'fields.social.employmentStatus.options.student' },
        { value: 'homemaker', label: 'fields.social.employmentStatus.options.homemaker' },
      ],
      namespace: 'social',
    },
    {
      id: 'income',
      label: 'fields.social.income.label',
      description: 'fields.social.income.description',
      type: FieldType.MULTI_SELECT,
      tags: ['financial', 'economic'],
      priority: 85,
      options: [
        { value: 'employment', label: 'fields.social.income.options.employment' },
        { value: 'disability', label: 'fields.social.income.options.disability' },
        { value: 'social_security', label: 'fields.social.income.options.social_security' },
        { value: 'pension', label: 'fields.social.income.options.pension' },
        { value: 'family_support', label: 'fields.social.income.options.family_support' },
        { value: 'public_assistance', label: 'fields.social.income.options.public_assistance' },
        { value: 'other', label: 'fields.social.income.options.other' },
      ],
      namespace: 'social',
    },
    {
      id: 'educationLevel',
      label: 'fields.social.educationLevel.label',
      description: 'fields.social.educationLevel.description',
      type: FieldType.SELECT,
      tags: ['education', 'professional'],
      priority: 80,
      options: [
        { value: 'not_enrolled', label: 'fields.social.educationLevel.options.not_enrolled' },
        { value: 'elementary', label: 'fields.social.educationLevel.options.elementary' },
        { value: 'middle_school', label: 'fields.social.educationLevel.options.middle_school' },
        { value: 'high_school', label: 'fields.social.educationLevel.options.high_school' },
        { value: 'college', label: 'fields.social.educationLevel.options.college' },
        { value: 'vocational', label: 'fields.social.educationLevel.options.vocational' },
        { value: 'special_education', label: 'fields.social.educationLevel.options.special_education' },
      ],
      namespace: 'social',
    },
    {
      id: 'insurance',
      label: 'fields.social.insurance.label',
      description: 'fields.social.insurance.description',
      type: FieldType.MULTI_SELECT,
      tags: ['financial', 'economic', 'health'],
      priority: 75,
      options: [
        { value: 'none', label: 'fields.social.insurance.options.none' },
        { value: 'private', label: 'fields.social.insurance.options.private' },
        { value: 'medicare', label: 'fields.social.insurance.options.medicare' },
        { value: 'medicaid', label: 'fields.social.insurance.options.medicaid' },
        { value: 'va', label: 'fields.social.insurance.options.va' },
        { value: 'marketplace', label: 'fields.social.insurance.options.marketplace' },
      ],
      namespace: 'social',
    },
    {
      id: 'supportNetwork',
      label: 'fields.social.supportNetwork.label',
      description: 'fields.social.supportNetwork.description',
      type: FieldType.MULTI_SELECT,
      tags: ['social', 'relationships', 'living-situation'],
      priority: 70,
      options: [
        { value: 'family', label: 'fields.social.supportNetwork.options.family' },
        { value: 'friends', label: 'fields.social.supportNetwork.options.friends' },
        { value: 'community', label: 'fields.social.supportNetwork.options.community' },
        { value: 'religious', label: 'fields.social.supportNetwork.options.religious' },
        { value: 'professional', label: 'fields.social.supportNetwork.options.professional' },
        { value: 'none', label: 'fields.social.supportNetwork.options.none' },
      ],
      namespace: 'social',
    },
    {
      id: 'servicesReceived',
      label: 'fields.social.servicesReceived.label',
      description: 'fields.social.servicesReceived.description',
      type: FieldType.MULTI_SELECT,
      tags: ['social', 'living-situation'],
      priority: 65,
      allowCustomOptions: true,
      options: [
        { value: 'case_management', label: 'fields.social.servicesReceived.options.case_management' },
        { value: 'counseling', label: 'fields.social.servicesReceived.options.counseling' },
        { value: 'housing_assistance', label: 'fields.social.servicesReceived.options.housing_assistance' },
        { value: 'food_assistance', label: 'fields.social.servicesReceived.options.food_assistance' },
        { value: 'transportation', label: 'fields.social.servicesReceived.options.transportation' },
        { value: 'healthcare', label: 'fields.social.servicesReceived.options.healthcare' },
        { value: 'childcare', label: 'fields.social.servicesReceived.options.childcare' },
        { value: 'legal_aid', label: 'fields.social.servicesReceived.options.legal_aid' },
      ],
      namespace: 'social',
    },
    {
      id: 'riskFactors',
      label: 'fields.social.riskFactors.label',
      description: 'fields.social.riskFactors.description',
      type: FieldType.MULTI_SELECT,
      tags: ['social', 'living-situation'],
      priority: 60,
      options: [
        { value: 'domestic_violence', label: 'fields.social.riskFactors.options.domestic_violence' },
        { value: 'substance_abuse', label: 'fields.social.riskFactors.options.substance_abuse' },
        { value: 'mental_health', label: 'fields.social.riskFactors.options.mental_health' },
        { value: 'homelessness', label: 'fields.social.riskFactors.options.homelessness' },
        { value: 'food_insecurity', label: 'fields.social.riskFactors.options.food_insecurity' },
        { value: 'unemployment', label: 'fields.social.riskFactors.options.unemployment' },
        { value: 'isolation', label: 'fields.social.riskFactors.options.isolation' },
        { value: 'legal_issues', label: 'fields.social.riskFactors.options.legal_issues' },
      ],
      namespace: 'social',
    },
    {
      id: 'strengths',
      label: 'fields.social.strengths.label',
      description: 'fields.social.strengths.description',
      type: FieldType.MULTI_SELECT,
      tags: ['social', 'living-situation'],
      priority: 55,
      allowCustomOptions: true,
      options: [
        { value: 'resilience', label: 'fields.social.strengths.options.resilience' },
        { value: 'motivated', label: 'fields.social.strengths.options.motivated' },
        { value: 'support_system', label: 'fields.social.strengths.options.support_system' },
        { value: 'stable_housing', label: 'fields.social.strengths.options.stable_housing' },
        { value: 'employed', label: 'fields.social.strengths.options.employed' },
        { value: 'good_health', label: 'fields.social.strengths.options.good_health' },
      ],
      namespace: 'social',
    },
    {
      id: 'socialNotes',
      label: 'fields.social.socialNotes.label',
      description: 'fields.social.socialNotes.description',
      type: FieldType.RICH_TEXT,
      tags: ['notes', 'documentation'],
      priority: 50,
      multiline: true,
      placeholder: 'fields.social.socialNotes.placeholder',
      namespace: 'social',
    },
  ],
};
